import { useEffect, useState } from 'react';
import api from '../../services/api';
import { useLocation } from 'react-router-dom';
import CityScreen from './CityScreen';
import { ITEM_TYPE_LABELS, getGalleryImageSrc } from './cityUtils';
import { useCityContext } from './useCityContext';
import { Coins, PlusCircle, X } from 'lucide-react';

function toPositiveInt(value, fallback = 1) {
  const n = Number(value);
  return Number.isInteger(n) && n > 0 ? n : fallback;
}

function isEquippedWeaponRow(row) {
  return Number(row?.item_type || -1) === 0 && Number(row?.equipped || 0) === 1;
}

function isActionDisabled(action, row) {
  return typeof action.disabled === 'function' ? action.disabled(row) : action.disabled;
}

function StorageColumn({
  title,
  rows,
  quantityByItem,
  setQuantityByItem,
  actions = [],
  capacityLabel = '',
  headerAction = null
}) {
  return (
    <section className="rounded-xl border border-cyan-500/30 bg-[#08101f] p-4 flex flex-col min-h-[420px] h-[62vh] xl:h-[70vh]">
      <div className="flex items-center justify-between gap-3 mb-3 shrink-0">
        <h2 className="font-bold text-cyan-300">{title}</h2>
        <div className="flex items-center gap-2">
          {capacityLabel && <span className="text-xs text-cyan-100/80 border border-cyan-500/20 bg-[#060c18] rounded-full px-2.5 py-1">{capacityLabel}</span>}
          {headerAction}
        </div>
      </div>
      <div className="space-y-2 overflow-y-auto pr-1 flex-1 min-h-0">
        {rows.length === 0 && <p className="text-slate-400 text-sm">Sin artículos.</p>}
        {rows.map((row) => (
          <article key={`${title}-${row.id_item}`} className="rounded-lg border border-slate-700 bg-[#060c18] p-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg border border-slate-700 bg-slate-900/60 overflow-hidden flex items-center justify-center">
                {getGalleryImageSrc(row) ? (
                  <img src={getGalleryImageSrc(row)} alt={row.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-[10px] text-slate-500">No img</span>
                )}
              </div>
              <div>
                <p className="text-cyan-200 font-bold">{row.name} x{row.quantity}</p>
                <p className="text-xs text-slate-400">
                  Tipo: {ITEM_TYPE_LABELS[Number(row.item_type)] || 'Objeto'} | Nivel req: {row.level_required || 1}
                </p>
                {Number(row.equipped || 0) === 1 && (
                  <p className="text-[11px] text-amber-300">Contiene unidades equipadas (no movibles esas unidades).</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                min={1}
                value={quantityByItem[row.id_item] ?? 1}
                onChange={(e) => setQuantityByItem((prev) => ({ ...prev, [row.id_item]: toPositiveInt(e.target.value, 1) }))}
                disabled={isEquippedWeaponRow(row)}
                className="w-16 bg-slate-900/70 border border-slate-700 rounded px-2 py-1 text-sm text-white"
              />
              <div className="flex flex-col gap-1">
                {actions.map((action) => (
                  <button
                    key={`${row.id_item}-${action.key}`}
                    onClick={() => action.onClick(row)}
                    disabled={isActionDisabled(action, row)}
                    className={`px-2 py-1 rounded text-xs border disabled:opacity-50 ${action.className}`}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

export default function CityWarehouse() {
  const location = useLocation();
  const { loading, error, context, setContext, cityTypeLabel } = useCityContext();
  const blocked = context?.access?.canUseWarehouse === false;
  const fromRpg = Boolean(location.state?.fromRpg);

  const [busy, setBusy] = useState(false);
  const [warehouseError, setWarehouseError] = useState('');
  const [groupScope, setGroupScope] = useState(null);
  const [capacities, setCapacities] = useState({
    personal: { used: 0, max: 30 },
    group: { used: 0, max: 50 },
    expansion: { step: 30, cost: 300, canAfford: false, alreadyPurchased: false },
    warehouseExpansion: { step: 30, cost: 300, canAfford: false, canExpand: false }
  });
  const [storages, setStorages] = useState({ backpack: [], personal: [], group: [] });
  const [qtyBackpack, setQtyBackpack] = useState({});
  const [qtyPersonal, setQtyPersonal] = useState({});
  const [qtyGroup, setQtyGroup] = useState({});
  const [upgradeModal, setUpgradeModal] = useState(null);

  const hasGroupStorage = Boolean(groupScope);
  const personalUpgradePurchased = Boolean(capacities.expansion?.alreadyPurchased);
  const canExpandGroupStorage = Boolean(hasGroupStorage && capacities.warehouseExpansion?.canExpand);

  async function loadWarehouse() {
    try {
      const res = await api.get('/robots/my-city-warehouse/state');
      setStorages(res.data?.storages || { backpack: [], personal: [], group: [] });
      setGroupScope(res.data?.groupScope || null);
      setCapacities(res.data?.capacities || {
        personal: { used: 0, max: 30 },
        group: { used: 0, max: 50 },
        expansion: { step: 30, cost: 300, canAfford: false, alreadyPurchased: false },
        warehouseExpansion: { step: 30, cost: 300, canAfford: false, canExpand: false }
      });
      if (res.data?.bot || res.data?.city || res.data?.access) {
        setContext((prev) => ({
          ...prev,
          bot: res.data?.bot || prev?.bot || null,
          city: res.data?.city || prev?.city || null,
          access: res.data?.access || prev?.access || null
        }));
      }
      setWarehouseError('');
    } catch (err) {
      setStorages({ backpack: [], personal: [], group: [] });
      setGroupScope(null);
      setWarehouseError(err.response?.data?.error || 'No se pudo cargar el almacén');
      throw err;
    }
  }

  useEffect(() => {
    loadWarehouse().catch(() => {});
  }, []);

  async function moveItem(from, to, row, qtyMap) {
    const quantity = toPositiveInt(qtyMap[row.id_item], 1);
    try {
      setBusy(true);
      await api.post('/robots/my-city-warehouse/move', {
        from,
        to,
        lines: [{ itemId: row.id_item, quantity }]
      });
      await loadWarehouse();
    } catch (err) {
      setWarehouseError(err.response?.data?.error || 'No se pudo mover el recurso');
    } finally {
      setBusy(false);
    }
  }

  async function expandPersonalStorage() {
    try {
      setBusy(true);
      await api.post('/robots/my-city-warehouse/expand-personal');
      await loadWarehouse();
    } catch (err) {
      setWarehouseError(err.response?.data?.error || 'No se pudo ampliar el almacén personal');
    } finally {
      setBusy(false);
    }
  }

  async function expandCityStorage() {
    try {
      setBusy(true);
      await api.post('/robots/my-city-warehouse/expand-city');
      await loadWarehouse();
    } catch (err) {
      setWarehouseError(err.response?.data?.error || 'No se pudo ampliar el almacén de ciudad');
    } finally {
      setBusy(false);
    }
  }

  function openUpgradeModal(type) {
    if (type === 'personal') {
      setUpgradeModal({
        type,
        title: 'Ampliar almacén personal',
        description: personalUpgradePurchased
          ? 'Este almacén ya fue ampliado una vez. El botón quedará desactivado.'
          : 'Puedes ampliar una sola vez tu almacén personal en +30 espacios por 300 oro.',
        cost: capacities.expansion.cost,
        canAfford: capacities.expansion.canAfford,
        confirmLabel: 'Comprar +30'
      });
      return;
    }

    setUpgradeModal({
      type,
      title: 'Ampliar almacén de ciudad',
      description: canExpandGroupStorage
        ? 'Este aumento solo afecta a la ciudad actual. Cada ciudad mantiene su propia capacidad.'
        : 'No puedes ampliar el almacén de ciudad en esta ubicación.',
      cost: capacities.warehouseExpansion.cost,
      canAfford: capacities.warehouseExpansion.canAfford,
      confirmLabel: 'Ampliar +30'
    });
  }

  async function confirmUpgradeModal() {
    if (!upgradeModal) return;
    const action = upgradeModal.type === 'personal' ? expandPersonalStorage : expandCityStorage;
    await action();
    setUpgradeModal(null);
  }

  if (loading) return <div className="min-h-screen bg-[#04060d] text-cyan-200 flex items-center justify-center">Cargando almacén...</div>;
  if (error) return <div className="min-h-screen bg-[#04060d] text-rose-200 flex items-center justify-center">{error}</div>;

  return (
    <CityScreen
      context={context}
      cityTypeLabel={cityTypeLabel}
      title={`Almacén - ${context.city?.name || 'Ciudad'}`}
      subtitle="Transferencia logística entre mochila, almacén personal y clan/subclan"
      backLinkLabel={fromRpg ? 'Volver a Cúpula' : 'Volver a Ciudad'}
      backLinkTarget={fromRpg ? '/rpg' : '/city'}
      showDashboardLink={!fromRpg}
    >
      {blocked && (
        <section className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="text-amber-200 text-sm">Acceso restringido: piloto neutral en ciudad de clan no puede usar Almacén.</p>
        </section>
      )}
      {warehouseError && <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{warehouseError}</div>}

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-5">
        <StorageColumn
          title="Mochila del BOT"
          rows={storages.backpack || []}
          quantityByItem={qtyBackpack}
          setQuantityByItem={setQtyBackpack}
          capacityLabel={`${capacities.backpack?.used ?? 0}/${capacities.backpack?.max ?? 0}`}
          actions={[
            {
              key: 'to-personal',
              label: 'A personal',
              onClick: (row) => moveItem('backpack', 'personal', row, qtyBackpack),
              disabled: (row) => busy || blocked || capacities.personal.used >= capacities.personal.max || isEquippedWeaponRow(row),
              className: 'border-cyan-400/60 text-cyan-200 bg-cyan-500/10'
            },
            {
              key: 'to-group',
              label: hasGroupStorage ? 'A grupo' : 'Sin grupo',
              onClick: (row) => moveItem('backpack', 'group', row, qtyBackpack),
              disabled: (row) => busy || blocked || !hasGroupStorage || capacities.group.used >= capacities.group.max || isEquippedWeaponRow(row),
              className: 'border-indigo-400/60 text-indigo-200 bg-indigo-500/10'
            }
          ]}
        />

        <StorageColumn
          title="Almacén personal"
          rows={storages.personal || []}
          quantityByItem={qtyPersonal}
          setQuantityByItem={setQtyPersonal}
          capacityLabel={`${capacities.personal.used}/${capacities.personal.max}`}
          headerAction={(
            <button
              onClick={() => openUpgradeModal('personal')}
              disabled={busy || personalUpgradePurchased || blocked}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-bold disabled:opacity-50 ${
                personalUpgradePurchased
                  ? 'border-slate-600 text-slate-400 bg-slate-800/60'
                  : 'border-emerald-400/60 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20'
              }`}
              title={personalUpgradePurchased ? 'Ya ampliado' : 'Ampliar +30 por 300 oro'}
            >
              <Coins size={14} />
              {personalUpgradePurchased ? 'Ampliado' : 'Ampliar'}
            </button>
          )}
          actions={[
            {
              key: 'to-backpack',
              label: 'A mochila',
              onClick: (row) => moveItem('personal', 'backpack', row, qtyPersonal),
              disabled: (row) => busy || blocked || isEquippedWeaponRow(row),
              className: 'border-cyan-400/60 text-cyan-200 bg-cyan-500/10'
            },
            {
              key: 'to-group',
              label: hasGroupStorage ? 'A grupo' : 'Sin grupo',
              onClick: (row) => moveItem('personal', 'group', row, qtyPersonal),
              disabled: (row) => busy || blocked || !hasGroupStorage || capacities.group.used >= capacities.group.max || isEquippedWeaponRow(row),
              className: 'border-indigo-400/60 text-indigo-200 bg-indigo-500/10'
            }
          ]}
        />

        <StorageColumn
          title="Almacén clan/subclan"
          rows={storages.group || []}
          quantityByItem={qtyGroup}
          setQuantityByItem={setQtyGroup}
          capacityLabel={`${capacities.group.used}/${capacities.group.max}`}
          actions={[
            {
              key: 'to-backpack',
              label: 'A mochila',
              onClick: (row) => moveItem('group', 'backpack', row, qtyGroup),
              disabled: (row) => busy || blocked || !hasGroupStorage || isEquippedWeaponRow(row),
              className: 'border-cyan-400/60 text-cyan-200 bg-cyan-500/10'
            },
            {
              key: 'to-personal',
              label: 'A personal',
              onClick: (row) => moveItem('group', 'personal', row, qtyGroup),
              disabled: (row) => busy || blocked || capacities.personal.used >= capacities.personal.max || isEquippedWeaponRow(row),
              className: 'border-indigo-400/60 text-indigo-200 bg-indigo-500/10'
            }
          ]}
        />
      </div>

      {upgradeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#01040acc] px-4">
          <div className="w-full max-w-lg rounded-2xl border border-cyan-500/30 bg-[#08101f] shadow-[0_0_40px_rgba(0,255,255,0.18)] overflow-hidden">
            <div className="flex items-start justify-between gap-3 border-b border-cyan-500/20 px-5 py-4">
              <div>
                <h3 className="text-xl font-bold text-cyan-200">{upgradeModal.title}</h3>
                <p className="text-sm text-slate-300 mt-1">Coste: {upgradeModal.cost} oro</p>
              </div>
              <button
                onClick={() => setUpgradeModal(null)}
                className="rounded-full border border-slate-700 p-2 text-slate-300 hover:text-white hover:border-cyan-400/60"
                aria-label="Cerrar modal"
              >
                <X size={16} />
              </button>
            </div>
            <div className="px-5 py-4 space-y-4">
              <p className="text-sm text-slate-200 leading-6">{upgradeModal.description}</p>
              <div className={`rounded-xl border p-3 text-sm ${upgradeModal.canAfford ? 'border-emerald-400/30 bg-emerald-500/10 text-emerald-200' : 'border-rose-400/30 bg-rose-500/10 text-rose-200'}`}>
                {upgradeModal.canAfford
                  ? 'Tienes oro suficiente para hacer esta ampliación.'
                  : 'No tienes oro suficiente para comprar esta ampliación.'}
              </div>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setUpgradeModal(null)}
                  className="px-4 py-2 rounded-lg border border-slate-700 text-slate-200 hover:border-slate-500"
                >
                  Cancelar
                </button>
                <button
                  onClick={confirmUpgradeModal}
                  disabled={busy || !upgradeModal.canAfford || (upgradeModal.type === 'personal' && personalUpgradePurchased) || (upgradeModal.type === 'city' && !canExpandGroupStorage)}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border font-bold disabled:opacity-50 ${
                    upgradeModal.canAfford
                      ? 'border-emerald-400/60 text-emerald-200 bg-emerald-500/10 hover:bg-emerald-500/20'
                      : 'border-rose-400/60 text-rose-200 bg-rose-500/10'
                  }`}
                >
                  <Coins size={16} />
                  {upgradeModal.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </CityScreen>
  );
}
